import {
  _HTMLElement,
  initializeProps,
  fromAttributeValue,
  toAttributeValue,
  applyAttribute,
  kebabToCamelCase,
} from '../utils';

/**
 * ### Reflected attributes
 * On a custom element, an attribute is reflected if it has a corresponding
 * property by the same name, getting and setting the attribute's value. This
 * mixin allows declaring attributes as reflected, automatically creating
 * correspoding properties on the resulting custom element.
 *
 * ### Attribute types
 * This mixin uses `attributeTypes` to translate attribute values when accessing
 * them via their reflected properties. If a type isn't declared for an
 * attribute, it defaults to `string` (equivalent to how the native
 * `getAttribute` and `setAttribute` methods behave, with the exception of
 * setting `null`).
 *
 * Supported types: `string`, `boolean` and `number`.
 *
 * For more detail on reflected attribute types, see [the spec](
 * https://html.spec.whatwg.org/multipage/common-dom-interfaces.html#reflecting-content-attributes-in-idl-attributes).
 *
 * ### Removing a reflected attribute
 * Setting a reflected attribute's property to `null` removes the attribute from
 * the given DOM element (equivalent to calling the native `removeAttribute`
 * method). Attributes with `boolean` type can also be removed by setting the property to `false`.
 *
 * @category mixin
 * @param {Class} [Base=HTMLElement] Optional base class to extend from.
 * @returns {Class}
 *
 * @example
 ```js
import { withAttributeReflection } from '@updater-legacy/components/custom-element';

class CustomInput extends withAttributeReflection() {
  static reflectedAttributes = ['name', 'disabled'];
  static attributeTypes = {
    name: 'string' // Optional declaration; 'string' is the default type.
    disabled: 'boolean'
  };
}
```

Allows reading attribute values using getters...

```html
<custom-input type="text" disabled></custom-input>
```
```js
console.log(customInput.type); // "text"
console.log(customInput.disabled); // true
```

...and writing attribute values using setters:

```js
customInput.type = "number";
customInput.disabled = false;
```
```html
<custom-input type="number"></custom-input>
```
*/
export const withAttributeReflection = (Base = _HTMLElement) =>
  class extends Base {
    constructor() {
      super();

      const reflectedAttrs = this.constructor.reflectedAttributes || [];
      const attributeTypes = this.constructor.attributeTypes || {};
      this._initialReflectedAttrs = initializeProps(
        reflectedAttrs.map(kebabToCamelCase),
        (attr) => this[attr]
      );

      // Create a getter/setter pair for each attribute
      reflectedAttrs.forEach((name) => {
        const propName = kebabToCamelCase(name);
        const type = attributeTypes[name] || 'string';
        const fromAttributeValueType = fromAttributeValue(type);
        const toAttributeValueType = toAttributeValue(type);

        Object.defineProperty(this, propName, {
          enumerable: true,
          get: () => fromAttributeValueType(this.getAttribute(name)),
          set: (newVal) => {
            // Only update if the new value is different from the current attribute
            // value, else we unnecessarily trigger `attributeChangedCallback`
            // (with same `oldVal` and `newVal).
            if (this[propName] !== newVal) {
              const attrVal = toAttributeValueType(newVal);
              applyAttribute(this, name, attrVal);
            }
          },
        });
      });
    }

    connectedCallback() {
      super.connectedCallback && super.connectedCallback();

      // Apply "upgradeProperty" workaround. While these are attributes we are
      // allowing users to set them as properties and if the related
      // properties are set before this constructor is called the setter won't
      // be called like we want it to be. We need to do this here because it
      // is speced that constructors must not add attributes.
      // https://developers.google.com/web/fundamentals/web-components/best-practices#lazy-properties
      // https://w3c.github.io/webcomponents/spec/custom/#custom-element-conformance
      if (this._initialReflectedAttrs) {
        Object.keys(this._initialReflectedAttrs).forEach((attr) => {
          if (this._initialReflectedAttrs[attr] !== undefined) {
            this[attr] = this._initialReflectedAttrs[attr];
          }
        });

        // We only want to do this once otherwise if this is connected then
        // disconnected all the attrs would be reset.
        delete this._initialReflectedAttrs;
      }
    }
  };
